import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import AppRangePicker, { DateRange } from "../../components/Core/AppRangePicker/AppRangePicker";
import AppText from "../../components/Core/AppText/AppText";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import ConsultantAutoComplete from "../../components/Core/AutoComplete/ConsultantAutoComplete";
import ConsultantManagerAutoComplete from "../../components/Core/AutoComplete/ConsultantManagerAutoComplete";
import ContractAutoComplete from "../../components/Core/AutoComplete/ContractAutoComplete";
import CustomerAutoComplete from "../../components/Core/AutoComplete/CustomerAutoComplete";
import Button from "../../components/Core/Button/Button";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import {
	EventType,
	Schedule,
	ScheduleRow,
	ZoomLevel,
} from "../../components/Shared/Schedule/Schedule";
import Tab from "../../components/Shared/Tabs/Tab";
import { ScheduleBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { JOB_STATUS } from "../../configs/constants/JobStatus";
import useQuery from "../../hooks/useQuery";
import useQueryState from "../../hooks/useQueryState";
import { useConsultantSchedule } from "../../networking/consultant.hooks";
import { useJobSchedule } from "../../networking/job.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { appTheme } from "../../theme/Theme";
import { getServiceName } from "../../utils/competence.utils";
import { consultantRoute, customerRoute, jobRoute } from "../../utils/route.utils";
import {
	getEventTypeForJobStatus,
	getEventTypeForTimeReport,
	getStartEndFromQuery,
} from "../../utils/schedule.util";
import Job from "../../models/Job";
import { filters } from "../../configs/schedule.config";

const SchedulePage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const query = useQuery();

	const [selectedFilter, setSelectedFilter] = useQueryState("t", {
		initialValue: filters[0].id,
	});

	const [zoomLevel, setZoomLevel] = useQueryState<ZoomLevel>("z", {
		initialValue: 2,
		valueFromString: (val) => Number(val) as ZoomLevel,
	});

	const [selectedDateRange, setSelectedDateRange] = useQueryState<{
		startDate: Date;
		endDate: Date;
	}>("r", {
		initialValue: getStartEndFromQuery(query),
		valueFromString: (val) => {
			if (!val) return;
			const json = JSON.parse(val);
			json.startDate = new Date(json.startDate);
			json.endDate = new Date(json.endDate);
			return json;
		},
		valueToString: (val) => (val ? JSON.stringify(val) : ""),
	});

	const [pool, setPool] = useQueryState<string>("pool", { initialValue: "" });

	const [customerId, setCustomerId] = useQueryState<string>("customer", { initialValue: "" });
	const [contractId, setContractId] = useQueryState<string>("contract", { initialValue: "" });
	const [consultantId, setConsultantId] = useQueryState<string>("consultant", {
		initialValue: "",
	});
	const [consultantManagerId, setConsultantManagerId] = useQueryState<string>("manager", {
		initialValue: "",
	});

	const [includeUnbooked, setIncludeUnbooked] = useQueryState<boolean>("includeUnbooked", {
		initialValue: false,
		valueFromString: (val) => val === "true",
		valueToString: (val) => (val ? "true" : "false"),
	});

	const { data: organisation, isLoading: isLoadingOrganization } = useOrganisation();

	const { jobSchedules: jobList, isLoading: isLoadingJobSchedules } = useJobSchedule(
		selectedDateRange?.startDate.toISOString(),
		selectedDateRange?.endDate.toISOString(),
		[customerId],
		[contractId],
		["customer", "applications.consultant"],
		["applications.consultant.absences", "applications.consultant.hasWorked"],
		selectedFilter === filters[0].id,
		consultantManagerId
	);

	const { consultantSchedules, isLoading: isLoadingConsultantSchedules } = useConsultantSchedule(
		selectedDateRange?.startDate.toISOString(),
		selectedDateRange?.endDate.toISOString(),
		[customerId],
		[contractId],
		["timeReports.job.customer"],
		["hasWorked"],
		selectedFilter === filters[1].id,
		consultantManagerId,
		includeUnbooked
	);

	const isDataLoading =
		isLoadingConsultantSchedules || isLoadingJobSchedules || isLoadingOrganization;

	const rows = useMemo(() => {
		if (selectedFilter === filters[0].id) {
			if (!jobList) return [];

			const jobListClone: Job[] = JSON.parse(JSON.stringify(jobList));

			return jobListClone
				.filter((job) => {
					if (consultantId) {
						const applicationForConsultant = (job.applications || []).find(
							(application) =>
								application.consultant.id === consultantId && application.status === "ACCEPTED"
						);

						if (!applicationForConsultant) return false;

						// Filter so only shifts that the consultant has applied for is shown
						job.shifts = job.shifts?.filter((shift) =>
							applicationForConsultant.shifts?.find((s) => s.id === shift.id)
						);

						return true;
					}
					return true;
				})
				.map<ScheduleRow>((job) => ({
					id: job.id,
					title: (
						<div className="flex flex-col items-end gap-0.5">
							<div
								className="text-right font-medium cursor-pointer text-sm leading-none"
								onClick={() => navigate(customerRoute(job.customer.id))}
							>
								{job.customer.name}
							</div>
							<div
								className="text-right cursor-pointer text-gray-500 text-sm leading-none"
								onClick={() => navigate(jobRoute(job.id))}
							>
								<>
									#{job.orderNumber},{" "}
									{getServiceName(organisation, job.competence.branch, job.competence.service)}
								</>
							</div>
						</div>
					),
					onClick: () => {},
					events: (job.shifts || []).map((shift) => ({
						...shift,
						id: shift.id!,
						type: getEventTypeForJobStatus(
							job.status,
							shift,
							// job.status === JOB_STATUS.BOOKED_WITH_UNMANNED_SHIFTS
							[JOB_STATUS.BOOKED_WITH_UNMANNED_SHIFTS, JOB_STATUS.BOOKED].includes(job.status)
								? job.applications?.find(
										(a) => a.status === "ACCEPTED" && a.shifts?.find((s) => s.id === shift.id)
								  )
								: undefined
						),
						job,
						shift,
					})),
				}));
		} else if (selectedFilter === filters[1].id) {
			if (consultantSchedules) {
				return consultantSchedules
					.sort((a, b) =>
						((a.firstName ?? "") + (a.lastName ?? "")).localeCompare(
							(b.firstName ?? "") + (b.lastName ?? "")
						)
					)
					.map<ScheduleRow>((consultantSchedule) => ({
						id: consultantSchedule.id,
						title: (
							<>
								<AppText
									textAlign="right"
									weight="medium"
									cursor="pointer"
									data-contact={consultantSchedule.id}
									onClick={() => navigate(consultantRoute(consultantSchedule.id))}
								>
									{(consultantSchedule.firstName + " " + consultantSchedule.lastName).trim()}
								</AppText>
							</>
						),
						onClick: () => {}, // TODO
						events: [
							...consultantSchedule.timeReports.map((tr) => ({
								id: tr.id,
								start: tr.reported.start,
								end: tr.reported.end,
								type: getEventTypeForTimeReport(tr),
								job: tr.job,
								timeReport: tr,
							})),
							...consultantSchedule.absences.map((absence) => ({
								id: absence.id,
								start: absence.startDate,
								end: absence.endDate,
								type: "absence" as EventType,
							})),
						],
					}));
			}
			return [];
		}
		return [];
	}, [jobList, selectedFilter, consultantSchedules, consultantId]);

	return (
		<>
			<Breadcrumbs
				items={ScheduleBreadcrumbs}
				showPoolDropdown
				onChangePool={(newPool) => setPool(newPool)}
				selectedPool={pool}
			/>
			<div className="p-4 px-6">
				<Flex margin="0 0 0.75rem 0" justifyContent="space-between" alignItems="flex-start">
					<Flex justifyContent="flex-start" alignItems="flex-start" gap="1.75rem">
						<Flex width="fit-content" gap="0.6rem">
							{filters.map((f) => (
								<Tab
									key={f.id}
									action={() => setSelectedFilter(f.id)}
									text={f.label}
									role="filter"
									active={f.id === selectedFilter}
								/>
							))}
						</Flex>
						<AppRangePicker
							placeholder={t("timeReports.selectDateRange")}
							value={
								selectedDateRange
									? {
											start: selectedDateRange.startDate,
											end: selectedDateRange.endDate,
									  }
									: undefined
							}
							onChange={(range?: DateRange) =>
								range &&
								setSelectedDateRange({
									startDate: range.start,
									endDate: range.end,
								})
							}
						/>

						<Flex alignItems="flex-start" gap="0.25rem" padding="2px 0 0 0">
							<Button
								icon="zoom-in"
								iconSize="20px"
								iconColor="black"
								lessPadding
								backgroundColor="white"
								border={`1px solid ${appTheme.colors.COLOR_DIVIDER_GRAY}`}
								disabled={zoomLevel === 4}
								onClick={() => setZoomLevel((zoomLevel + 1) as ZoomLevel)}
							/>
							<Button
								icon="zoom-out"
								iconSize="20px"
								iconColor="black"
								lessPadding
								backgroundColor="white"
								border={`1px solid ${appTheme.colors.COLOR_DIVIDER_GRAY}`}
								disabled={zoomLevel === 1}
								onClick={() => setZoomLevel((zoomLevel - 1) as ZoomLevel)}
							/>
						</Flex>
						<Flex justifyContent="flex-end" gap="0.5rem">
							{selectedFilter === filters[0].id && (
								<ConsultantAutoComplete
									onChange={setConsultantId}
									value={consultantId}
									pool={pool}
									placeholder="schedule.filterConsultant"
									width={170}
									icon="filter"
									hidePrefix
								/>
							)}
							<ConsultantManagerAutoComplete
								onChange={setConsultantManagerId}
								value={consultantManagerId}
								placeholder="schedule.filterConsultantManager"
								width={170}
								icon="filter"
								hidePrefix
							/>
							<CustomerAutoComplete
								onChange={setCustomerId}
								value={customerId}
								pool={pool}
								placeholder="schedule.filterCustomer"
								width={170}
								icon="filter"
								hidePrefix
							/>
							<ContractAutoComplete
								onChange={setContractId}
								value={contractId}
								placeholder="schedule.filterContract"
								width={170}
								icon="filter"
								hidePrefix
							/>
						</Flex>
					</Flex>
				</Flex>

				{selectedFilter === filters[1].id && (
					<Flex margin="0.25rem 0 0 0" justifyContent="flex-end" alignItems="center">
						<Checkbox
							label="schedule.includeUnbooked"
							isChecked={includeUnbooked}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setIncludeUnbooked(e.target.checked)
							}
						/>
					</Flex>
				)}

				<Flex margin="1rem 0">
					<Schedule
						range={selectedDateRange}
						rows={rows}
						zoomLevel={Number(zoomLevel) as ZoomLevel}
						selectedFilter={selectedFilter}
						isLoading={isDataLoading}
						jobSchedules={jobList}
						filterData={{
							customerIdList: [customerId],
							contractIdList: [contractId],
							consultantIdList: [consultantId],
						}}
					/>
				</Flex>
			</div>
		</>
	);
};

export default SchedulePage;
